import { Hit, SearchResponse } from "@algolia/client-search";
import algoliasearch, { SearchIndex } from "algoliasearch";
import { useLang } from "../hooks/LangHooks";
import { useLocSettings } from "../hooks/SettingsHooks";
import { useMemo } from "react";
import { getCurrentSite } from "../utils/SiteUtils";
import { useQuery } from "react-query";
import { enhanceProduct } from "../utils/CockpitUtils";
import { enhanceProductSpecifications } from "../utils/SpecsUtils";
import { getProductBySku } from "../utils/ProductUtils";
import { useUserProducts } from "../hooks/ProductHooks";
import { Product } from "../types/Product";
import cloneDeep from "lodash/cloneDeep";
import { unlocked } from "../utils/TypeUtils";
import { Language } from "../config/languages";
import { Settings } from "../types/Settings";

export const searchClient = algoliasearch(
  import.meta.env.ALGOLIA_APP_ID || "6AGQ8N0SOD",
  import.meta.env.ALGOLIA_API_KEY || "3bf1904b8983f1f131d54272a5adf49e"
);

export function getSearchIndex(
  name: string,
  site: string,
  lang: string
): SearchIndex {
  const indexName = [name, site, lang].join("_");
  return searchClient.initIndex(indexName);
}

export type ProductHit = Hit<{
  sku: string;
  name: string;
  unavailable: boolean;
  data: Product;
}>;

export type ProductCategoryHit = Hit<{
  name: string;
}>;

export type FaqArticleHit = Hit<{
  title: string;
}>;

type AlgoliaProductSearch = {
  categories: string[];
  faqArticles: string[];
  products: Product[];
  skus: string[];
};

export async function algoliaProductSearch(
  query: string,
  limit = 1000, lang: Language, activeProducts: Product[], settings: Settings
): Promise<AlgoliaProductSearch> {
  const queryString = query.trim();
  const site = getCurrentSite();
  const indexName = (prefix: string) => [prefix, site, lang].join("_");
  const _search =
    async () => {
      if (queryString?.length < 3) {
        return null;
      }
      const response = await searchClient.multipleQueries([
        {
          indexName: indexName("Products"),
          query: query,
          params: {
            hitsPerPage: limit
          }
        },
        {
          indexName: indexName("ProductCategories"),
          query: query,
          params: {
            hitsPerPage: limit
          }
        },
        {
          indexName: indexName("FaqArticles"),
          query: query,
          params: {
            hitsPerPage: limit
          }
        }
      ]);
      return {
        products: response.results[0] as SearchResponse<ProductHit>,
        product_categories: response
          .results[1] as SearchResponse<ProductCategoryHit>,
        faqArticles: response.results[2] as SearchResponse<FaqArticleHit>
      };
    };

    const data = await _search()
    if (data == null) {
      return {
        skus: [],
        products: [],
        categories: [],
        faqArticles: []
      };
    } else {
      const wrappedProducts = data.products.hits;
      const skus = wrappedProducts.map(product => product.sku);
      const products: Product[] = wrappedProducts
        .map(wrapper => wrapper.data)
        .map(cloneDeep)
        .map(_product => {
          const activeProduct = getProductBySku(activeProducts, _product.sku);
          if (activeProduct && activeProduct.stock && activeProduct.price) {
            const product = unlocked(_product);
            product.stock = activeProduct.stock;
            product.price = activeProduct.price;
          }
          return _product;
        })
        .map(enhanceProduct)
        .map(product => enhanceProductSpecifications(product, settings));
      const categories = data.product_categories.hits.map(hit => hit.objectID);
      const faqArticles = data.faqArticles.hits.map(hit => hit.objectID);
      return {
        skus,
        categories,
        products,
        faqArticles
      };
    }
}

export function useAlgoliaProductSearch(
  query: string,
  limit = 1000
): AlgoliaProductSearch {
  const queryString = query.trim();
  const site = getCurrentSite();
  const lang = useLang();
  const activeProducts = useUserProducts();
  const settings = useLocSettings();
  const indexName = (prefix: string) => [prefix, site, lang].join("_");
  const { data } = useQuery(
    [queryString, "algolia_products_search", limit, lang],
    async () => {
      if (queryString?.length < 3) {
        return null;
      }
      const response = await searchClient.multipleQueries([
        {
          indexName: indexName("Products"),
          query: query,
          params: {
            hitsPerPage: limit
          }
        },
        {
          indexName: indexName("ProductCategories"),
          query: query,
          params: {
            hitsPerPage: limit
          }
        },
        {
          indexName: indexName("FaqArticles"),
          query: query,
          params: {
            hitsPerPage: limit
          }
        }
      ]);
      return {
        products: response.results[0] as SearchResponse<ProductHit>,
        product_categories: response
          .results[1] as SearchResponse<ProductCategoryHit>,
        faqArticles: response.results[2] as SearchResponse<FaqArticleHit>
      };
    },
    { refetchInterval: 1000 * 60 * 15 }
  );

  return useMemo((): AlgoliaProductSearch => {
    if (data == null) {
      return {
        skus: [],
        products: [],
        categories: [],
        faqArticles: []
      };
    } else {
      const wrappedProducts = data.products.hits;
      const skus = wrappedProducts.map(product => product.sku);
      const products: Product[] = wrappedProducts
        .map(wrapper => wrapper.data)
        .map(cloneDeep)
        .map(_product => {
          const activeProduct = getProductBySku(activeProducts, _product.sku);
          if (activeProduct && activeProduct.stock && activeProduct.price) {
            const product = unlocked(_product);
            product.stock = activeProduct.stock;
            product.price = activeProduct.price;
          }
          return _product;
        })
        .map(enhanceProduct)
        .map(product => enhanceProductSpecifications(product, settings));
      const categories = data.product_categories.hits.map(hit => hit.objectID);
      const faqArticles = data.faqArticles.hits.map(hit => hit.objectID);
      return {
        skus,
        categories,
        products,
        faqArticles
      };
    }
  }, [data, activeProducts, settings]);
}

export async function algoliaFaqSearch(
  query: string,
  limit = 10, lang: Language
): Promise<{faqArticles: string[]}> {
  const queryString = query.trim();
  const site = getCurrentSite();
  const indexName = ["FaqArticles", site, lang].join("_");
  const _search =
    async () => {
      if (queryString?.length < 3) {
        return null;
      }
      const response = await searchClient.search([
        {
          indexName,
          query: query,
          params: {
            hitsPerPage: limit
          }
        }
      ]);
      const faqArticles =
        (response.results[0] as SearchResponse<FaqArticleHit>) || [];
      return {
        faqArticles
      };
    };

    const data = await _search()
    if (data == null) {
      return {
        faqArticles: []
      };
    } else {
    const faqArticles = (data?.faqArticles?.hits || []).map(
      hit => hit.objectID
    );
    return { faqArticles };
    }
}


export function useAlgoliaFaqArticleSearch(
  query: string,
  limit: number = 1000
): { faqArticles: string[] } {
  const queryString = query.trim();
  const site = getCurrentSite();
  const lang = useLang();
  const indexName = ["FaqArticles", site, lang].join("_");
  const { data } = useQuery(
    [queryString, "algolia_faq_articles_search", limit, lang],
    async () => {
      if (queryString?.length < 3) {
        return null;
      }
      const response = await searchClient.search([
        {
          indexName,
          query: query,
          params: {
            hitsPerPage: limit
          }
        }
      ]);
      const faqArticles =
        (response.results[0] as SearchResponse<FaqArticleHit>) || [];
      return {
        faqArticles
      };
    },
    { refetchInterval: 1000 * 60 * 15 }
  );
  return useMemo(() => {
    const faqArticles = (data?.faqArticles?.hits || []).map(
      hit => hit.objectID
    );
    return { faqArticles };
  }, [data]);
}
